import type { ReactNode } from 'react'

/* Layout primitives. This module exports ONLY components — shared types and
   helpers live in ../types.ts so React Fast Refresh keeps working. */

interface SectionCardProps {
  title?: ReactNode
  subtitle?: ReactNode
  /** Right-aligned affordance in the header, e.g. "View all →". */
  action?: ReactNode
  /** Drop the body padding — for cards whose content is a table or tile grid. */
  flush?: boolean
  children?: ReactNode
  className?: string
}

/**
 * The standard bordered card with an optional hairline-separated header.
 * Used for every panel on Overview and every detail panel.
 */
export function SectionCard({ title, subtitle, action, flush, children, className }: SectionCardProps) {
  const classes = className ? `lh-card ${className}` : 'lh-card'
  return (
    <section className={classes}>
      {(title || action) && (
        <div className="lh-section-header">
          <div style={{ minWidth: 0 }}>
            {title && <h2 className="lh-section-title">{title}</h2>}
            {subtitle && <p className="lh-section-subtitle">{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      {flush ? children : <div className="lh-card-body">{children}</div>}
    </section>
  )
}

/**
 * A page-level title in the display serif.
 *
 * EB Garamond only above 18px (small x-height, delicate strokes) — this is one
 * of the few places it appears. Section-card titles stay in Inter.
 */
export function PageTitle({ children, subtitle }: { children: ReactNode; subtitle?: ReactNode }) {
  return (
    <div>
      <h1 className="lh-display-title">{children}</h1>
      {subtitle && <p className="lh-section-subtitle">{subtitle}</p>}
    </div>
  )
}

/**
 * A section heading used OUTSIDE a card — for pages that group content into
 * labelled bands rather than panels.
 *
 * Stays in Inter, not the display serif: at section-heading size the serif reads
 * weak (EB Garamond is reserved for 18px+), and a page should have one display
 * title, not five.
 */
export function SectionTitle({ children, subtitle }: { children: ReactNode; subtitle?: ReactNode }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <h2 className="lh-section-title" style={{ fontSize: 15 }}>{children}</h2>
      {subtitle && <p className="lh-section-subtitle">{subtitle}</p>}
    </div>
  )
}

/** Right-aligned header affordance. Renders as a button, or a link when `href` is set. */
export function SectionAction({ children, href, onClick }: { children: ReactNode; href?: string; onClick?: () => void }) {
  if (href) {
    return <a className="lh-section-action" href={href}>{children}</a>
  }
  return <button type="button" className="lh-section-action" onClick={onClick}>{children}</button>
}

/** Hairline divider — lighter than a border; for use inside a card. */
export function HairlineDivider() {
  return <hr className="lh-hairline" />
}

/** Responsive panel grid. Backs the three-panel detail layouts. */
export function PanelGrid({ children }: { children: ReactNode }) {
  return <div className="lh-panel-grid">{children}</div>
}

interface MetaItem {
  label: ReactNode
  value: ReactNode
  /** Render the value in the mono face — for run ids, correlation ids, hashes. */
  mono?: boolean
}

/**
 * Label/value pairs. Backs Execution Summary, Record Summary, Execution Health
 * and Report Information — four panels from one primitive.
 */
export function MetaList({ items }: { items: MetaItem[] }) {
  return (
    <dl className="lh-meta-list">
      {items.map((item, i) => (
        <div className="lh-meta-row" key={i}>
          <dt className="lh-meta-label">{item.label}</dt>
          <dd className="lh-meta-value" data-mono={item.mono ? 'true' : undefined}>{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

/** Grid wrapper for CountTile — hairline gaps come from the grid background. */
export function CountTileGrid({ children }: { children: ReactNode }) {
  return <div className="lh-count-grid">{children}</div>
}

interface CountTileProps {
  value: number | string
  label: ReactNode
  hint?: ReactNode
  action?: ReactNode
}

/** Big-number tile with a review affordance. Backs the Governance Queue tiles. */
export function CountTile({ value, label, hint, action }: CountTileProps) {
  return (
    <div className="lh-count-tile">
      <span className="lh-count-value">{value}</span>
      <span className="lh-count-label">{label}</span>
      {hint && <span className="lh-count-hint">{hint}</span>}
      {action}
    </div>
  )
}
